import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  createCategoryRequest,
  getCategoryRequestTypes,
  getHistory,
  getMyRequestList,
  getRequestList,
} from '../../actions/api/marketServiceRequest';
import type { RootState } from '../..';

export interface CatalogueRequestType {
  catalogueName: string;
  categoryRequestTypes: string[];
}

export interface RequestItem {
  serviceRequestId: number;
  catalogueName: string;
  requestType: string;
  requestName: string;
  requestDescription: string;
  relatedCategoryNames?: string;
  requestStatus: string;
  retailUnitName?: string;
  createdUser: string;
  updatedDate: string;
  registeredDate: string;
  approverComments?: string | null;
}

export interface RequestListResponse {
  content: RequestItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface HistoryEvent {
  serviceRequestStatus: string;
  updatedDate: string;
  userName: string;
  approverComments?: string | null;
}

export interface HistoryCommentsResponse {
  content: HistoryEvent[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
}
interface MarketRequestCategoryState {
  requestTypes: CatalogueRequestType[];
  listOfRequests: RequestListResponse | null;
  myListOfRequests: RequestListResponse | null;
  historyComments: HistoryCommentsResponse | null;
  loading: boolean;
}

const initialState: MarketRequestCategoryState = {
  requestTypes: [],
  listOfRequests: null,
  myListOfRequests: null,
  historyComments: null,
  loading: false,
};

const marketRequestCategorySlice = createSlice({
  name: 'marketServiceRequest',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          getCategoryRequestTypes.pending,
          createCategoryRequest.pending,
          getRequestList.pending,
          getMyRequestList.pending,
          getHistory.pending,
        ),
        (state) => {
          state.loading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoryRequestTypes.fulfilled,
          createCategoryRequest.fulfilled,
          getRequestList.fulfilled,
          getMyRequestList.fulfilled,
          getHistory.fulfilled,
        ),
        (state, action) => {
          state.loading = false;
          if (action.type === getCategoryRequestTypes.fulfilled.type) {
            state.requestTypes = action.payload || [];
          }
          if (action.type === getRequestList.fulfilled.type) {
            const nextPageData = action.payload || null;
            const requestArg = action.meta.arg as { pageNo?: number } | undefined;
            const requestedPageNo = requestArg?.pageNo ?? 0;

            if (!nextPageData) {
              state.listOfRequests = null;
              return;
            }

            if (requestedPageNo > 0 && state.listOfRequests) {
              state.listOfRequests = {
                ...nextPageData,
                content: [...state.listOfRequests.content, ...nextPageData.content],
              };
              return;
            }

            state.listOfRequests = nextPageData;
          }
          if (action.type === getMyRequestList.fulfilled.type) {
            const nextPageData = action.payload || null;
            const requestArg = action.meta.arg as { pageNo?: number } | undefined;
            const requestedPageNo = requestArg?.pageNo ?? 0;

            if (!nextPageData) {
              state.myListOfRequests = null;
              return;
            }

            if (requestedPageNo > 0 && state.myListOfRequests) {
              state.myListOfRequests = {
                ...nextPageData,
                content: [
                  ...state.myListOfRequests.content,
                  ...nextPageData.content,
                ],
              };
              return;
            }

            state.myListOfRequests = nextPageData;
          }
          if (action.type === getHistory.fulfilled.type) {
            state.historyComments = action.payload || null;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoryRequestTypes.rejected,
          createCategoryRequest.rejected,
          getRequestList.rejected,
          getMyRequestList.rejected,
          getHistory.rejected,
        ),
        (state) => {
          state.loading = false;
        },
      );
  },
});

export default marketRequestCategorySlice.reducer;

export const selectRequestTypes = (state: RootState) =>
  state.marketServiceRequest.requestTypes;

export const selectRequestList = (state: RootState) =>
  state.marketServiceRequest.listOfRequests;

export const selectMyRequestList = (state: RootState) =>
  state.marketServiceRequest.myListOfRequests;

export const selectHistoryComments = (state: RootState) =>
  state.marketServiceRequest.historyComments;
